import { useCampaignSlug } from '@/components/Functional/hooks-core/useCampaignSlug'
import { AppMachineContext, useRaundLifeCycleLoadingStatus } from '@/components/Functional/hooks-core/useRaundLifeCycle'
import {
  useTanstackRewardsFetchFn,
  useTanstackUserRewardsFetchFn,
} from '@/components/Functional/hooks-core/useTanstackFetchFn'
import { mergeRewardsWithUserData } from '@/components/Functional/lib/api/rewards'
import { openInNewTab } from '@/components/Functional/utils/openInNewTab'
import { resolveProgressTexts } from '@/components/Functional/utils/rewardCondition'
import { time } from '@/components/Functional/utils/util'
import { BottomNavigation } from '@/components/Layout/BottomNavigation'
import { Header } from '@/components/Layout/Header'
import { ArPhotoOverlay } from '@/components/Models/ar-photo'
import { DialogLoginRequired } from '@/components/Models/dialog/components/DialogLoginRequired'
import { DialogUseCoupon } from '@/components/Models/dialog/components/DialogUseCoupon'
import RewardsQuestionnaire from '@/components/Pages/rewards/components/RewardsQuestionnaire'
import { LotteryDrawingOverlay } from '@/components/Pages/rewards/components/detail/LotteryDrawingOverlay'
import { RewardActionButton } from '@/components/Pages/rewards/components/detail/RewardActionButton'
import { RewardDetailContent } from '@/components/Pages/rewards/components/detail/RewardDetailContent'
import { RewardDetailSkeleton } from '@/components/Pages/rewards/components/detail/RewardDetailSkeleton'
import { RewardDetailStateMachineDebug } from '@/components/Pages/rewards/components/detail/RewardDetailStateMachineDebug'
import type { TabItem } from '@/components/Pages/rewards/components/detail/RewardDetailTabs'
import { DialogV2ApplicationForm } from '@/components/Pages/rewards/components/dialogs-v2/DialogV2ApplicationForm'
import { DialogV2ApplyComplete } from '@/components/Pages/rewards/components/dialogs-v2/DialogV2ApplyComplete'
import { DialogV2ApplyConfirm } from '@/components/Pages/rewards/components/dialogs-v2/DialogV2ApplyConfirm'
import { DialogV2ApplyFailed } from '@/components/Pages/rewards/components/dialogs-v2/DialogV2ApplyFailed'
import { DialogV2CouponQrScan } from '@/components/Pages/rewards/components/dialogs-v2/DialogV2CouponQrScan'
import { DialogV2DlcAcquired } from '@/components/Pages/rewards/components/dialogs-v2/DialogV2DlcAcquired'
import { DialogV2LotteryLose } from '@/components/Pages/rewards/components/dialogs-v2/DialogV2LotteryLose'
import {
  DialogV2LotteryResultList,
  type LotteryResultItem,
} from '@/components/Pages/rewards/components/dialogs-v2/DialogV2LotteryResultList'
import { DialogV2LotteryWin } from '@/components/Pages/rewards/components/dialogs-v2/DialogV2LotteryWin'
import { DialogV2PrizeExchange } from '@/components/Pages/rewards/components/dialogs-v2/DialogV2PrizeExchange'
import { DialogV2SpotSelect } from '@/components/Pages/rewards/components/dialogs-v2/DialogV2SpotSelect'
import { DialogV2StampConsumptionConfirm } from '@/components/Pages/rewards/components/dialogs-v2/DialogV2StampConsumptionConfirm'
import type { PrizeData } from '@/schemas/v2/reward'
import type { UserRewardStatusDetailData } from '@/schemas/v2/user'
import { useQuery } from '@tanstack/react-query'
import { useSelector } from '@xstate/react'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRewardFlowStateMachine } from './useRewardFlowStateMachine'

/**
 * 一覧ダイアログの当選行ボタンラベルを prize.type / fulfillmentType / 現在のステータスから生成する。
 * ハズレ行は呼び出し側で別途 span("はずれ") に置き換えるため本関数には渡さない。
 * Table B-1 / B-2 / B-3 に準拠。
 */
function getLotteryDialogLabel(
  prizeType: PrizeData['type'] | undefined,
  fulfillmentType: PrizeData['fulfillmentType'] | undefined,
  overallStatus: UserRewardStatusDetailData['status'] | undefined,
  prizeSelectionType: UserRewardStatusDetailData['prizeSelectionType'],
): string {
  const isExchanged = overallStatus === 'EXCHANGED' || overallStatus === 'USED'
  const isAchieved = overallStatus === 'ACHIEVED'

  // ✅ 修正: DLC → '特典にアクセスする'
  if (prizeType === 'DLC') return 'アクセスする'
  if (prizeType === 'AR_PHOTO') {
    // NORMAL + EXCHANGED/USED → 「賞品を受け取る」
    if (isExchanged && prizeSelectionType !== 'LOTTERY') return '賞品を受け取る'
    return '起動する'
  }
  // ✅ 修正: GIFTEE → '特典を獲得する'
  if (prizeType === 'GIFTEE') return '特典を獲得する'
  // ✅ 修正: GIFT_CODE → '特典を獲得する'
  if (prizeType === 'GIFT_CODE') return '特典を獲得する'
  if (prizeType === 'COUPON') {
    // ✅ 修正: COUPON 未使用 → 'クーポンを使用する'
    return isExchanged ? '使用済み' : '使用する'
  }
  if (fulfillmentType === 'APPLICATION_FORM') {
    // ✅ 修正: APPLICATION_FORM + ACHIEVED → '特典に応募する'（enabled）、EXCHANGED/APPLIED → '応募済み'
    return isExchanged || overallStatus === 'APPLIED' ? '応募済み' : '応募する'
  }
  if (fulfillmentType === 'CLERK_REDEMPTION') {
    if (isExchanged) return '獲得済み'
    return '獲得する'
  }
  // ✅ 修正: ITEM + NONE（default）→ '特典を獲得する'（EXCHANGED は '交換済み'）
  if (isExchanged) return '交換済み'
  if (isAchieved) return '獲得する'
  return '特典を獲得する'
}

/**
 * 一覧ダイアログの当選行ボタンを活性化するかどうかを判定する。
 * Table B-1 / B-2 / B-3 に準拠。
 * - GIFT_CODE / DLC / AR_PHOTO: 常に活性
 * - COUPON: EXCHANGED / USED 以外は活性（NONE fulfillmentType）
 * - GIFTEE: EXCHANGED / USED 以外は活性
 * - CLERK_REDEMPTION（ITEM）: EXCHANGED / USED 以外は活性
 * - APPLICATION_FORM: EXCHANGED / USED / APPLIED 以外は活性（ACHIEVED は再応募可能）
 */
function isLotteryDialogButtonActive(
  prizeType: PrizeData['type'] | undefined,
  fulfillmentType: PrizeData['fulfillmentType'] | undefined,
  overallStatus: UserRewardStatusDetailData['status'] | undefined,
): boolean {
  if (prizeType === 'GIFT_CODE' || prizeType === 'DLC' || prizeType === 'AR_PHOTO') return true
  const isExchanged = overallStatus === 'EXCHANGED' || overallStatus === 'USED'
  if (prizeType === 'COUPON') return !isExchanged
  if (prizeType === 'GIFTEE') return !isExchanged
  if (fulfillmentType === 'CLERK_REDEMPTION') return !isExchanged
  // ITEM+NONE: ダイアログボタンから交換を実行できる（EXCHANGED / USED 以外は活性）
  if (prizeType === 'ITEM' && (fulfillmentType === 'NONE' || fulfillmentType == null)) return !isExchanged
  // APPLICATION_FORM: ACHIEVED は再応募可能（enabled）、EXCHANGED / APPLIED は non-active
  if (fulfillmentType === 'APPLICATION_FORM') return !isExchanged && overallStatus !== 'APPLIED'
  return false
}

// ─── 内部コンポーネント ───

type MachineViewProps = {
  rewardId: number
  isAuthenticated: boolean
  /** AppMachine が phase3 に到達し認証状態が確定しているかどうか */
  isAppReady: boolean
  enabled: boolean
  groupName: string | null
  tabs: TabItem[]
  activeTabIndex: number
  onTabChange: (index: number) => void
  loginUrl: string
  onPeriodOut: () => void
}

const RewardsDetailMachineView = ({
  rewardId,
  isAuthenticated,
  isAppReady,
  enabled,
  groupName,
  tabs,
  activeTabIndex,
  onTabChange,
  loginUrl,
  onPeriodOut,
}: MachineViewProps) => {
  const flow = useRewardFlowStateMachine({ rewardId, isAuthenticated, enabled })
  const {
    state,
    send,
    reward,
    userRewardStatus,
    transaction,
    errorMessage,
    serverValidationErrors,
    pendingFormData,
    applicationForm,
    applicationFormTransactionId,
    completedVia,
    pendingSpotId,
    isLoading,
    isLoginRequired,
    isUserStatusLoading,
    arPhotoFrames,
    arPhotoFramesReady,
    isPeriodOut,
  } = flow

  const [isLotteryResultListOpen, setIsLotteryResultListOpen] = useState(false)
  // ダイアログ内の特定クーポン行をクリックした際に保持する prize（DialogUseCoupon に渡す）
  const [pendingCouponPrize, setPendingCouponPrize] = useState<PrizeData | null>(null)

  const { t, i18n } = useTranslation()

  // arNavigate state かつ fetch 完了後に AR カメラオーバーレイを開く。
  // PhotoFrame は mount 時に一度だけフレームを読み込むため、fetch 完了前に mount すると空フレームになる。
  const arPhotoOpen = state.matches('arNavigate') && arPhotoFramesReady

  // reward データがなく initialLoading 中のみページ全体スケルトンを表示する。
  // キャッシュ経由で reward が先に取得できた場合はコンテンツを即時表示し、
  // ボタン部分だけスケルトンにする（isUserStatusLoading）。
  const isPageLoading = state.matches('initialLoading') && !reward

  const isLotteryOrArPhotoLose =
    userRewardStatus?.status === 'LOST' ||
    (userRewardStatus?.status === 'ACHIEVED' &&
      userRewardStatus?.prizeSelectionType === 'LOTTERY' &&
      ((userRewardStatus?.prizes ?? []).length === 0 || (userRewardStatus?.prizes ?? []).every((p) => p.isLotteryLost)))

  const progressTexts: string[] = userRewardStatus ? resolveProgressTexts(userRewardStatus.conditions) : []

  const prizeName =
    transaction?.prize?.name ??
    (userRewardStatus?.prizes ?? []).find((p) => p.prize?.type === 'ITEM')?.prize?.name ??
    null

  // 抽選当たりダイアログのアクションボタン文言を賞品種別から導出
  const lotteryWinReceiveLabel = (() => {
    const type = transaction?.prize?.type
    const fulfillmentType = transaction?.prize?.fulfillmentType
    if (type === 'DLC') return 'アクセスする'
    if (type === 'AR_PHOTO') return '起動する'
    if (type === 'COUPON') return '使用する'
    if (fulfillmentType === 'APPLICATION_FORM') return '応募する'
    return '獲得する'
  })()
  const giftCode = transaction?.prize?.code ?? null
  const gifteeUrl = transaction?.gifteeBoxUrl ?? null
  // acquire 直後は transaction.prize.couponSpots、ダイアログ内クーポン行選択時は pendingCouponPrize、
  // リロード後は userRewardStatus.prizes から解決
  const couponSpots: { id: number; name: string }[] =
    pendingCouponPrize?.couponSpots ??
    transaction?.prize?.couponSpots ??
    (userRewardStatus?.prizes ?? []).find((p) => !p.isLotteryLost && p.prize?.couponSpecificSpots)?.prize
      ?.couponSpots ??
    []

  // DLC コンテンツURL: 初回獲得時は transaction から（DLC type のときのみ）、ページリロード後は userRewardStatus.prizes から解決
  const dlcContentFile =
    (transaction?.prize?.type === 'DLC' ? (transaction.prize.contentFile ?? null) : null) ??
    (userRewardStatus?.prizes ?? []).find((p) => p.prize?.type === 'DLC')?.prize?.contentFile ??
    null

  // memo との組み合わせで不要な再レンダーを防ぐため useCallback で安定化
  const handleAcquire = useCallback(() => send({ type: 'ACQUIRE_BUTTON_PRESSED' }), [send])
  // ArPhotoOverlay の useEffect([open, onClose]) に渡すため参照を安定化する。
  // インライン arrow function を使うと arPhotoFrames 更新時の再レンダリングで参照が変わり、
  // クリーンアップ内の history.back() が意図せず発火して画面が閉じてしまう。
  const handleArOverlayClosed = useCallback(() => send({ type: 'AR_OVERLAY_CLOSED' }), [send])

  // DLC: dlcAcquired ステートに入ったらダイアログを挟まず自動的にダウンロード＋交換へ進む
  const isDlcAcquired = state.matches('dlcAcquired')
  useEffect(() => {
    if (!isDlcAcquired) return
    if (dlcContentFile) openInNewTab(dlcContentFile)
    send({ type: 'DLC_DOWNLOAD' })
  }, [isDlcAcquired, dlcContentFile, send])

  // DLC: 交換完了ステートに入ったらダイアログを挟まず自動的にリロードへ進む
  // リロード後は context.transaction が null になるため userRewardStatus.prizes でもフォールバック判定
  const isDlcComplete =
    state.matches('complete') &&
    (transaction?.prize?.type === 'DLC' || (userRewardStatus?.prizes ?? []).some((p) => p.prize?.type === 'DLC'))
  useEffect(() => {
    if (!isDlcComplete) return
    send({ type: 'DIALOG_CLOSED' })
  }, [isDlcComplete, send])

  // 期間外または存在しない特典へのアクセス: ロード完了後 reward が null の場合はキャンペーントップへ
  useEffect(() => {
    if (isPeriodOut) {
      onPeriodOut()
    }
  }, [isPeriodOut, onPeriodOut])

  // ACHIEVED+COUPON の直接遷移時は transaction が null のため prizes からフォールバック
  // ダイアログ内のクーポン行クリック時は pendingCouponPrize を最優先で使用
  const couponPrize: PrizeData | null =
    pendingCouponPrize ??
    (transaction?.prize?.type === 'COUPON' ? transaction.prize : null) ??
    (userRewardStatus?.prizes ?? []).find((p) => p.prize?.type === 'COUPON')?.prize ??
    null
  const couponName = couponPrize?.name ?? prizeName ?? ''
  const couponImage = couponPrize?.image ?? transaction?.prize?.image ?? null
  const rawCouponStartAt = couponPrize?.startAt ?? transaction?.prize?.startAt ?? null
  const rawCouponEndAt = couponPrize?.endAt ?? transaction?.prize?.endAt ?? null
  const couponStartAt = rawCouponStartAt ? (time({ time: rawCouponStartAt, language: i18n.language }) ?? null) : null
  const couponEndAt = rawCouponEndAt ? (time({ time: rawCouponEndAt, language: i18n.language }) ?? null) : null

  // 交換完了ダイアログに表示する商品名（COUPON は couponPrize.name、その他は prizeName）
  const completePrizeName: string | null = couponPrize?.name ?? prizeName ?? null
  // 交換完了ダイアログに表示するスポット名（スポット記録型クーポン選択後のみ解決される）
  const completedSpotName: string | null =
    pendingSpotId != null ? (couponSpots.find((s) => s.id === pendingSpotId)?.name ?? null) : null

  // 物品（ITEM）受け取り確認ダイアログ用の賞品画像
  const itemPrizeImage: string | null =
    (transaction?.prize?.type === 'ITEM' ? (transaction.prize.image ?? null) : null) ??
    (userRewardStatus?.prizes ?? []).find((p) => p.prize?.type === 'ITEM')?.prize?.image ??
    null

  // 一覧キャッシュに groupName がない場合は詳細 API の reward.groupName にフォールバック
  const resolvedGroupName = groupName ?? (reward?.groupType !== 'NONE' ? (reward?.groupName ?? null) : null)

  const lotteryResultItems = useMemo((): LotteryResultItem[] => {
    const overallStatus = userRewardStatus?.status
    return (userRewardStatus?.prizes ?? []).map((p) => {
      const prizeType = p.prize?.type
      const fulfillmentType = p.prize?.fulfillmentType
      // 個別 prize の status を使うことで、複数回特典で EXCHANGED/ACHIEVED が混在する場合に正しくラベル・活性を判定する
      // p.status が optional のため、未提供時は overallStatus をフォールバックとして使用する
      const prizeStatus = p.status ?? overallStatus
      const active = !p.isLotteryLost && isLotteryDialogButtonActive(prizeType, fulfillmentType, prizeStatus)
      return {
        id: p.userRewardTransactionId,
        result: p.isLotteryLost ? 'lose' : 'win',
        date: p.acquiredAt ? new Date(p.acquiredAt).toLocaleDateString('ja-JP') : '',
        label: p.isLotteryLost
          ? undefined
          : getLotteryDialogLabel(prizeType, fulfillmentType, prizeStatus, userRewardStatus?.prizeSelectionType),
        onReceive: active
          ? () => {
              if (p.prize?.type === 'COUPON') {
                setPendingCouponPrize(p.prize)
              }
              setIsLotteryResultListOpen(false)
              send({ type: 'DIALOG_EXCHANGE_PRESSED', transactionId: p.userRewardTransactionId })
            }
          : undefined,
      }
    })
  }, [userRewardStatus?.prizes, userRewardStatus?.status, userRewardStatus?.prizeSelectionType, send])

  const isLotteryType = userRewardStatus?.prizeSelectionType === 'LOTTERY'
  const showLotteryResultButton = (userRewardStatus?.isRepeatable ?? false) && lotteryResultItems.length > 0

  // DLC の場合、EXCHANGED でもバッジは「達成」を表示する
  const badgeStatus = (() => {
    const s = userRewardStatus?.status
    if (s === 'EXCHANGED' && userRewardStatus?.prizes.some((p) => p.prize?.type === 'DLC')) {
      return 'ACHIEVED' as const
    }
    return s
  })()

  // スタンプ消費確認ダイアログに渡す消費数
  const stampConsumptionCount =
    userRewardStatus?.conditions.find((c) => c.type === 'STAMP_COUNT')?.threshold ?? reward?.stamps?.length ?? 0

  return (
    <>
      {isPageLoading ? (
        <RewardDetailSkeleton />
      ) : !reward ? (
        <p className="p-4 text-center text-[13px] text-gray-400">特典情報が見つかりません</p>
      ) : (
        <RewardDetailContent
          image={reward.image}
          name={reward.name}
          status={badgeStatus}
          showLotteryDoneLabel={isLotteryOrArPhotoLose}
          progressTexts={progressTexts}
          description={reward.description ?? null}
          stamps={reward.stamps}
          groupName={resolvedGroupName}
          tabs={tabs.length >= 2 ? tabs : undefined}
          activeTabIndex={activeTabIndex}
          onTabChange={onTabChange}
        >
          <RewardActionButton
            isLoading={isLoading}
            isDataLoading={isUserStatusLoading || !isAppReady}
            userRewardStatus={userRewardStatus}
            isAuthenticated={isAuthenticated}
            onAcquire={handleAcquire}
            hasGiftCodeInMemory={transaction?.prize?.type === 'GIFT_CODE'}
          />
          {showLotteryResultButton && (
            <button
              type="button"
              onClick={() => setIsLotteryResultListOpen(true)}
              className="mt-4 w-full h-[36px] rounded-[8px] border border-[#757575] bg-white text-[13px] font-bold text-[#757575]"
            >
              {isLotteryType ? '抽選結果一覧' : '獲得賞品一覧'}
            </button>
          )}
        </RewardDetailContent>
      )}

      {/* ── スタンプ消費確認ダイアログ ── */}
      <DialogV2StampConsumptionConfirm
        opened={state.matches('stampConsumptionConfirm')}
        onClose={() => send({ type: 'DIALOG_CLOSED' })}
        onConfirm={() => send({ type: 'STAMP_CONSUMPTION_CONFIRMED' })}
        consumptionCount={stampConsumptionCount}
        rewardName={reward?.name ?? null}
        prizeSelectionType={userRewardStatus?.prizeSelectionType ?? null}
      />

      {/* ── アンケート ── */}
      <RewardsQuestionnaire
        opened={state.matches('questionnaireCheck')}
        onAnswered={() => send({ type: 'QUESTIONNAIRE_ANSWERED' })}
        onDismissed={() => send({ type: 'QUESTIONNAIRE_DISMISSED' })}
      />

      {/* ── 抽選中 GIF オーバーレイ（LOTTERY 型のみ） ── */}
      <LotteryDrawingOverlay
        visible={state.matches('acquiring') && userRewardStatus?.prizeSelectionType === 'LOTTERY'}
      />

      {/* ── AR 獲得ダイアログ（DLC はダイアログなしで自動ダウンロード） ── */}
      <DialogV2DlcAcquired
        opened={state.matches('arPhotoAcquired')}
        onClose={() => send({ type: 'DIALOG_CLOSED' })}
        prizeName={prizeName ?? undefined}
        mode="ar"
        onArCameraStart={() => send({ type: 'AR_CAMERA_START' })}
      />

      {/* ── 抽選結果「当たり」ダイアログ ── */}
      <DialogV2LotteryWin
        opened={state.matches('lotteryWin')}
        onClose={() => send({ type: 'DIALOG_CLOSED' })}
        prizeName={prizeName}
        onReceive={() => send({ type: 'LOTTERY_RECEIVE' })}
        receiveLabel={lotteryWinReceiveLabel}
      />

      {/* ── 抽選結果「はずれ」ダイアログ ── */}
      <DialogV2LotteryLose opened={state.matches('lotteryLose')} onClose={() => send({ type: 'DIALOG_CLOSED' })} />

      {/* ── 商品受け取り確認ダイアログ（全 ITEM 特典・fulfillmentType 問わず） ── */}
      <DialogUseCoupon
        opened={state.matches('itemConfirm')}
        onClose={() => send({ type: 'DIALOG_CLOSED' })}
        couponName={prizeName ?? ''}
        couponImage={itemPrizeImage}
        title={t('mynavi.dialog.staff_confirmation_title', 'スタッフ確認用画面')}
        warningText={t('mynavi.dialog.staff_warning', '賞品を受取るときは必ずスタッフに\nこの画面を見せてください')}
        swipeText={t('mynavi.dialog.swipe_to_receive', '右にスワイプで受け取る')}
        isLoading={isLoading}
        onUseCoupon={() => send({ type: 'ITEM_CONFIRM' })}
      />

      {/* ── 応募フォームダイアログ ── */}
      {applicationForm && (
        <DialogV2ApplicationForm
          key={applicationFormTransactionId ?? 'none'}
          opened={state.matches('applicationForm')}
          applicationForm={applicationForm}
          onSubmit={(formData) => send({ type: 'APPLICATION_FORM_SUBMIT', formData })}
          onClose={() => send({ type: 'DIALOG_CLOSED' })}
          serverErrors={serverValidationErrors ?? undefined}
        />
      )}

      {/* ── 応募確認ダイアログ ── */}
      <DialogV2ApplyConfirm
        opened={state.matches('applicationFormConfirm')}
        onClose={() => send({ type: 'DIALOG_CLOSED' })}
        onConfirm={() => send({ type: 'APPLICATION_FORM_CONFIRM' })}
        formData={pendingFormData ?? undefined}
        fields={applicationForm?.fields ?? []}
      />

      {/* ── QRコード読み取り画面（スポット指定クーポン消込・1段階目） ── */}
      <DialogV2CouponQrScan
        opened={state.matches('couponSpotSelect')}
        onClose={() => send({ type: 'DIALOG_CLOSED' })}
        onRead={(spotId) => send({ type: 'COUPON_SPOT_SELECTED', spotId })}
        onManualSelect={() => send({ type: 'MANUAL_SPOT_SELECT_OPENED' })}
      />

      {/* ── スポット選択ダイアログ（QR読み取り不可時のフォールバック） ── */}
      <DialogV2SpotSelect
        opened={state.matches('couponSpotSelectManual')}
        onClose={() => send({ type: 'DIALOG_CLOSED' })}
        spots={couponSpots}
        onSelect={(spotId) => send({ type: 'COUPON_SPOT_SELECTED', spotId })}
      />

      {/* ── クーポン使用確認ダイアログ ── */}
      <DialogUseCoupon
        opened={state.matches('couponUse')}
        onClose={() => {
          setPendingCouponPrize(null)
          send({ type: 'DIALOG_CLOSED' })
        }}
        couponName={couponName}
        couponImage={couponImage}
        usagePeriodFrom={couponStartAt}
        usagePeriodTo={couponEndAt}
        title={t('mynavi.dialog.staff_confirmation_title', 'スタッフ確認用画面')}
        warningLine1={t('reward_detail.coupon.before_press_button_01', 'クーポンを使用するときに必ずスタッフに')}
        warningLine2={t('reward_detail.coupon.before_press_button_02', 'この画面を見せてください。')}
        swipeText={t('mynavi.dialog.swipe_to_use_coupon', '右にスワイプで使用')}
        isLoading={isLoading}
        onUseCoupon={() => send({ type: 'COUPON_EXCHANGE' })}
        spotName={completedSpotName}
      />

      {/* ── ギフトコード / Giftee 表示ダイアログ ── */}
      <DialogV2PrizeExchange
        opened={state.matches('giftCodeDisplay') || state.matches('gifteeRedirect')}
        onClose={() => send({ type: 'DIALOG_CLOSED' })}
        giftCode={giftCode}
        gifteeUrl={gifteeUrl}
        prizeName={prizeName}
        onConfirm={() => send({ type: 'EXCHANGE_CONFIRMED' })}
      />

      {/* ── 応募完了ダイアログ ── */}
      <DialogV2ApplyComplete
        opened={state.matches('complete')}
        onClose={() => send({ type: 'DIALOG_CLOSED' })}
        mode={completedVia ?? 'exchange'}
        prizeName={completedVia === 'exchange' ? completePrizeName : undefined}
        spotName={completedVia === 'exchange' ? completedSpotName : undefined}
        isCoupon={completedVia === 'exchange' ? couponPrize != null : undefined}
      />

      {/* ── エラーダイアログ ── */}
      <DialogV2ApplyFailed
        opened={state.matches('error')}
        onClose={() => send({ type: 'DIALOG_CLOSED' })}
        message={errorMessage ?? undefined}
        onRetry={() =>
          send({
            type: applicationFormTransactionId != null ? 'RETRY_APPLICATION_FORM' : 'RETRY',
          })
        }
      />

      {/* ── 非認証ログイン誘導ダイアログ ── */}
      <DialogLoginRequired
        opened={isLoginRequired}
        loginUrl={loginUrl}
        onClose={() => send({ type: 'DIALOG_CLOSED' })}
      />

      {/* ── AR カメラオーバーレイ ── */}
      <ArPhotoOverlay
        open={arPhotoOpen}
        photoFrames={arPhotoFrames}
        onClose={handleArOverlayClosed}
        onSaveSuccess={handleArOverlayClosed}
        snsHashtag=""
      />

      {/* ── 抽選結果一覧 / 獲得賞品一覧ダイアログ（isRepeatable=true かつ履歴あり） ── */}
      <DialogV2LotteryResultList
        opened={isLotteryResultListOpen}
        onClose={() => setIsLotteryResultListOpen(false)}
        results={lotteryResultItems}
        mode={isLotteryType ? 'lottery' : 'acquired'}
      />

      {/* ── ステートマシンデバッグ（開発環境のみ）── */}
      {process.env.NODE_ENV !== 'production' && (
        <RewardDetailStateMachineDebug flow={flow} isAuthenticated={isAuthenticated} enabled={enabled} />
      )}
    </>
  )
}

// ─── エクスポートコンポーネント ───

export const RewardsDetailPage = ({ id }: { id: number }) => {
  const router = useRouter()
  const { gen, loginUrl } = useCampaignSlug()

  const appActorRef = AppMachineContext.useActorRef()
  const isAuthenticated = useSelector(appActorRef, (s) => s.context.isAuthenticated)
  // AppMachine が phase3（checkAuth 完了後）に到達するまで isAuthenticated を false として扱う。
  // hydration 直後は AppMachine が phase1 にあり isAuthenticated = false の状態から始まるため、
  // appReady になる前に machine へ false を渡すと、その後の true への変化で機械が再作成され
  // コードが表示されない問題が発生する。appReady 後は isAuthenticated が確定済みなので安全。
  const { status: lifecycleStatus } = useRaundLifeCycleLoadingStatus()
  const isAppReady = lifecycleStatus === 'loaded'
  const machineIsAuthenticated = isAppReady ? isAuthenticated : false

  const initialRewardId = id
  const isValidId = initialRewardId > 0
  const queryEnabled = isValidId

  // タブ切り替えの override を管理する。URL の id が変わったら override を破棄する。
  const [activeOverride, setActiveOverride] = useState<{ forUrlId: number; rewardId: number } | null>(null)
  // override が現在の URL id 向けのものであれば使い、違えば URL id にフォールバック
  const activeRewardId =
    activeOverride !== null && activeOverride.forUrlId === initialRewardId
      ? activeOverride.rewardId
      : isValidId
        ? initialRewardId
        : 0

  // 特典一覧を取得してグループ内の特典を特定する（キャッシュ優先）
  const rewardsListQuery = useTanstackRewardsFetchFn()
  const userRewardsListQuery = useTanstackUserRewardsFetchFn()
  const { data: rewardsListRawData } = useQuery({ ...rewardsListQuery, enabled: queryEnabled })
  const { data: userRewardsListData } = useQuery({ ...userRewardsListQuery, enabled: queryEnabled && isAuthenticated })
  const rewardsListData = useMemo(
    () =>
      mergeRewardsWithUserData(
        rewardsListRawData ?? { rewards: [] },
        isAuthenticated ? (userRewardsListData ?? { rewards: [] }) : { rewards: [] },
      ),
    [rewardsListRawData, userRewardsListData, isAuthenticated],
  )

  const groupRewards = useMemo(() => {
    // activeRewardId と同じ groupId を持つ特典を抽出（groupId が null の場合は空）
    const current = rewardsListData.rewards.find((r) => r.id === activeRewardId)
    const gid = current?.groupId ?? null
    if (!gid) return []
    return rewardsListData.rewards.filter((r) => r.groupId === gid)
  }, [activeRewardId, rewardsListData.rewards])

  const tabs: TabItem[] = useMemo(() => groupRewards.map((r) => ({ id: r.id, label: r.name })), [groupRewards])

  const activeTabIndex = useMemo(
    () =>
      Math.max(
        0,
        groupRewards.findIndex((r) => r.id === activeRewardId),
      ),
    [groupRewards, activeRewardId],
  )

  const currentInList = useMemo(
    () =>
      groupRewards.find((r) => r.id === activeRewardId) ??
      rewardsListData.rewards.find((r) => r.id === activeRewardId) ??
      null,
    [groupRewards, activeRewardId, rewardsListData.rewards],
  )
  const groupName = currentInList?.groupType !== 'NONE' ? (currentInList?.groupName ?? null) : null

  const handleTabChange = useCallback(
    (index: number) => {
      const next = groupRewards[index]
      if (next && next.id !== activeRewardId) {
        setActiveOverride({ forUrlId: initialRewardId, rewardId: next.id })
      }
    },
    [groupRewards, activeRewardId, initialRewardId],
  )

  // 無効な id の場合は /rewards にリダイレクト（副作用は effect で実行）
  useEffect(() => {
    if (!isValidId) {
      void router.replace(gen('/rewards'))
    }
  }, [isValidId, router, gen])

  // 期間外または存在しない特典へのアクセスはキャンペーントップへリダイレクト
  const handlePeriodOut = useCallback(() => {
    void router.replace(gen('/'))
  }, [router, gen])

  if (!isValidId) {
    return null
  }

  return (
    <>
      <Header title="特典詳細" showBack onBack={() => router.push(gen('/rewards'))} showNotification={false} />
      <main className="pb-[72px]">
        {/* key={activeRewardId} でタブ切り替え時にステートマシンを再起動 */}
        <RewardsDetailMachineView
          key={activeRewardId}
          rewardId={activeRewardId}
          isAuthenticated={machineIsAuthenticated}
          isAppReady={isAppReady}
          enabled={queryEnabled}
          groupName={groupName}
          tabs={tabs}
          activeTabIndex={activeTabIndex}
          onTabChange={handleTabChange}
          loginUrl={loginUrl}
          onPeriodOut={handlePeriodOut}
        />
      </main>
      <BottomNavigation activeItem="reward" />
    </>
  )
}
